export function exportToCSV({ data, headerMap = {}, sheetName }) {
  if (!data || data.length === 0) {
    console.error('No data to export.');
    return;
  }

  const localHeaders = Object.keys(data[0]);
  const headers = localHeaders.map((header) => headerMap[header] || header);

  const csvContent = [
    headers.join(','),
    ...data.map((col) =>
      localHeaders.map((header) => removeCommas(col[header])).join(','),
    ),
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
  const link = document.createElement('a');

  link.href = window.URL.createObjectURL(blob);
  link.download = `${sheetName}.csv`;

  document.body.appendChild(link);
  link.click();
  link.remove();
}

// Like exportToExcel, but writes multiple sheets into one workbook and
// triggers a single download — for report-level exports where each sheet
// comes from a different attachment instead of one table.
export function exportWorkbook({
  sheets,
  fileName,
  exportedBy,
  dateRange,
  reportType,
}) {
  const validSheets = (sheets || []).filter((s) => s?.rows?.length);
  if (!validSheets.length) {
    console.error('No data to export.');
    return;
  }

  try {
    const XLSX = require('xlsx');
    const wb = XLSX.utils.book_new();

    for (const { name, rows, context } of validSheets) {
      const info = [
        ['Exported by', exportedBy],
        ...(!!dateRange ? [['Report Period', dateRange]] : []),
        ['Exported On', new Date().toLocaleString()],
        reportType,
        ...(context?.length ? [[], ...context] : []),
        [],
        Object.keys(rows[0]),
        ...rows.map((row) => Object.values(row)),
      ];

      const ws = XLSX.utils.json_to_sheet(info, { skipHeader: true });
      ws['!cols'] = Array.from(
        { length: XLSX.utils.decode_range(ws['!ref']).e.c + 1 },
        () => ({ width: 20 }),
      );
      XLSX.utils.book_append_sheet(wb, ws, name);
    }

    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    const link = document.createElement('a');

    link.href = window.URL.createObjectURL(blob);
    link.download = `${fileName}.xlsx`;

    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (error) {
    console.error('Error during export:', error);
  }
}

function removeCommas(value) {
  if (typeof value === 'string' && value.includes(',')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

export function exportToExcel({
  data,
  headerMap = {},
  sheetName,
  exportedBy,
  dateRange,
  type,
  fileName,
  reportType,
  errorCallback,
  workspace,
}) {
  if (!data || data.length === 0) {
    console.error('No data to export.');
    return;
  }

  try {
    const XLSX = require('xlsx');
    const info = [
      ['Exported by', exportedBy],
      ...(!!dateRange ? [[`${type} Period`, dateRange]] : []),
      ['Exported On', new Date().toLocaleString()],
      reportType,
      ...(!!workspace ? [['Workspace', `${workspace}`]] : []),
      [],
      Object.keys(data[0]).map((key) => headerMap[key] || key),
      ...data.map((row) => Object.values(row)),
    ];

    const ws = XLSX.utils.json_to_sheet(info, { skipHeader: true });

    const wb = XLSX.utils.book_new();

    ws['!cols'] = Array.from(
      { length: XLSX.utils.decode_range(ws['!ref']).e.c + 1 },
      () => ({ width: 20 }),
    );

    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });

    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    const link = document.createElement('a');

    link.href = window.URL.createObjectURL(blob);
    link.download = `${fileName}.xlsx`;

    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (error) {
    console.error('Error during export:', error);
    if (errorCallback) errorCallback(error);
  }
}
